import { useState, React } from 'react';
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Link,
  styled,
  Tooltip
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { formatAppointment } from 'utils/formatter';
import StatusPill from 'components/StatusPill';
import { OverflowText } from 'styles/GlobalStyle';

const StyledDataGrid = styled(DataGrid)(() => ({
  '& .MuiDataGrid-columnHeader, .MuiDataGrid-toolbarContainer': {
    backgroundColor: '#f0f0f0'
  }
}));

const DispatchTableView = (props) => {
  const {
    orders,
    transportationMode,
    fnViewOrderHandler,
    fnEditOrderHandler,
    fnDeleteOrderHandler,
    fnOrderStatsHandler,
    fnFileUploadHandler,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const toggleActionMenu = (evt, order) => {
    setSelectedOrder(order);
    const target = evt ? evt.currentTarget : null;
    setAnchorEl(target);
  };

  const INTERMODAL_COLUMNS = [
    {
      flex: 0.5,
      field: 'displayOrderId', headerName: 'Id', renderCell: (params) => {
        return (
          <Link variant='body1' component='button' underline='always' sx={{ fontWeight: 600 }}
            onClick={() => fnViewOrderHandler(params.row)}
          >
            {params.row.displayOrderId}
          </Link>
        );
      }
    },
    { field: 'orderType', headerName: 'Type', flex: 1 },
    {
      field: 'broker',
      headerName: 'Broker',
      flex: 1,
      renderCell: (params) => {
        const { name, address } = params.row.broker;
        return (<Tooltip
          title={<Typography variant='body1'>{`${address.city}, ${address.state}, ${address.country}`}</Typography>} arrow>
          <OverflowText style={{ textDecoration: 'underline dotted' }} >{name}</OverflowText>
        </Tooltip>)
      },
    },
    { field: 'brokerOrderNumber', headerName: 'Broker Order Num.', flex: 1 },
    {
      field: 'origin',
      headerName: 'Origin',
      flex: 1,
      renderCell: ({ row: { legs } }) => {
        const origins = legs && legs.length > 0 ? legs[0].origins : [];
        const locations = origins.map(loc => <Tooltip key={loc.name} title={<Typography variant='body1'>{`${loc.address.street}, ${loc.address.city}, ${loc.address.state}, ${loc.address.country}`}</Typography>} arrow>
          <OverflowText style={{ textDecoration: 'underline dotted' }}>{loc.name}</OverflowText>
        </Tooltip>);
        return <div>{locations}</div>;
      },
    },
    {
      field: 'origin_appointment',
      headerName: 'Appointment',
      flex: 1,
      renderCell: ({ row: { legs } }) => {
        const origins = legs && legs.length > 0 ? legs[0].origins : [];
        const appointments = origins.map((loc) => <OverflowText key={loc.name}>{formatAppointment(loc.appointment)}</OverflowText>)
        return appointments;
      },
    },
    {
      field: 'destination',
      headerName: 'Destination',
      flex: 1,
      renderCell: ({ row: { legs } }) => {
        const destinations = legs && legs.length > 0 ? legs[0].destinations : [];;
        const locations = destinations.map(loc => <Tooltip key={loc.name} title={<Typography variant='body1'>{`${loc.address.street}, ${loc.address.city}, ${loc.address.state}, ${loc.address.country}`}</Typography>} arrow>
          <OverflowText style={{ textDecoration: 'underline dotted' }}>{loc.name}</OverflowText>
        </Tooltip>);
        return <div>{locations}</div>;
      },
    },
    {
      field: 'dest_appointment',
      headerName: 'Appointment',
      flex: 1,
      renderCell: ({ row: { legs } }) => {
        const destinations = legs && legs.length > 0 ? legs[0].destinations : [];
        const appointments = destinations.map((loc) => <OverflowText key={loc.name}>{formatAppointment(loc.appointment)}</OverflowText>);
        return <div>{appointments}</div>
      },
    },
    {
      field: 'container',
      headerName: 'Container Num.',
      flex: 1,
      valueGetter: (value, row) =>
        row.container && row.container.deliveryContainerNumber,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        return <StatusPill
          category={'status'}
          status={params.value}
          label={params.value}
          size='large'
        />
      }
    },
  ];

  const OTR_COLUMNS = [
    {
      flex: 0.5,
      field: 'displayOrderId', headerName: 'Id', renderCell: (params) => {
        return (
          <Link variant='body1' component='button' underline='always' sx={{ fontWeight: 600 }} onClick={() => fnViewOrderHandler(params.row)}>
            {params.row.loadId}
          </Link>
        );
      }
    },
    { field: 'loadType', headerName: 'Type', flex: 1 },
    {
      field: 'broker',
      headerName: 'Broker',
      flex: 1,
      renderCell: (params) => {
        const { name, address } = params.row.broker;
        return (<Tooltip
          title={<Typography variant='body1'>{`${address.city}, ${address.state}, ${address.country}`}</Typography>} arrow>
          <span style={{ textDecoration: 'underline dotted' }}>{name}</span>
        </Tooltip>)
      },
    },
    { field: 'brokerOrderNumber', headerName: 'LoadNumber(Broker)', flex: 1 },
    {
      field: 'origin',
      headerName: 'Origin',
      flex: 1,
      renderCell: ({ row: { legs } }) => {
        const origins = legs && legs.length > 0 ? legs[0].origins : [];;
        const locations = origins.map(loc => <Tooltip key={loc.name} title={<Typography variant='body1'>{`${loc.address.street}, ${loc.address.city}, ${loc.address.state}, ${loc.address.country}`}</Typography>} arrow>
          <OverflowText style={{ textDecoration: 'underline dotted' }}>{loc.name}</OverflowText>
        </Tooltip>);
        return <div>{locations}</div>;
      },
    },
    {
      field: 'origin_appointment',
      headerName: 'Appointment',
      flex: 1,
      renderCell: ({ row: { legs } }) => {
        const origins = legs && legs.length > 0 ? legs[0].origins : [];
        const appointments = origins.map((loc) => <OverflowText key={loc.name}>{formatAppointment(loc.appointment)}</OverflowText>)
        return appointments;
      },
    },
    {
      field: 'destination',
      flex: 1,
      headerName: 'Destination',
      renderCell: ({ row: { legs } }) => {
        const destinations = legs && legs.length > 0 ? legs[0].destinations : [];;
        const locations = destinations.map(loc => <Tooltip key={loc.name} title={<Typography variant='body1'>{`${loc.address.street}, ${loc.address.city}, ${loc.address.state}, ${loc.address.country}`}</Typography>} arrow>
          <OverflowText style={{ textDecoration: 'underline dotted' }}>{loc.name}</OverflowText>
        </Tooltip>);
        return <div>{locations}</div>;
      },
    },
    {
      field: 'dest_appointment',
      headerName: 'Appointment',
      flex: 1,
      renderCell: ({ row: { legs } }) => {
        const destinations = legs && legs.length > 0 ? legs[0].destinations : [];
        const appointments = destinations.map((loc) => <OverflowText key={loc.name}>{formatAppointment(loc.appointment)}</OverflowText>)
        return <div>{appointments}</div>
      },
    },
    {
      field: 'trailerNumber',
      headerName: 'Trailer Num.',
      flex: 1,
    },
    {
      field: 'trailerLicense',
      headerName: 'Trailer License',
      flex: 1,
    },
    {
      field: 'truck',
      headerName: 'Truck',
      flex: 1,
      renderCell: (params) => {
        const { truckNumber, licensePlate, truckType } = params.row.truck ? params.row.truck : {};
        return <div>
          <Typography>{truckNumber}</Typography>
          <Typography>{licensePlate}</Typography>
        </div>
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        return <StatusPill
          category={'status'}
          status={params.value}
          label={params.value}
          size='large'
        />
      }
    },
  ];
  const ACTIONS = [
    {
      field: 'action',
      headerName: 'Action',
      headerClassName: 'action',
      flex: 0.5,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              aria-label='settings'
              onClick={(evt) => toggleActionMenu(evt, params.row)}
              size="large">
              <MoreVertIcon />
            </IconButton>
            <Menu
              id='long-menu'
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={() => toggleActionMenu(null, null)}
            >
              <MenuItem
                key={`edit:${params.row.id}`}
                onClick={() => fnEditOrderHandler(selectedOrder)}
              >
                <EditOutlinedIcon fontSize='small' />
                &nbsp;&nbsp;Edit Order
              </MenuItem>
              <Divider />
              <MenuItem
                key={`delete:${params.row.id}`}
                onClick={() => fnDeleteOrderHandler(selectedOrder)}
              >
                <DeleteOutlinedIcon fontSize='small' />
                &nbsp;&nbsp;Delete Order
              </MenuItem>
              <Divider />
              <MenuItem
                key={`files:${params.row.id}`}
                onClick={() => fnFileUploadHandler(selectedOrder)}
              >
                <AttachFileIcon fontSize='small' />
                &nbsp;&nbsp;Upload Documents
              </MenuItem>
              <Divider />
              <MenuItem
                key={`stats:${params.row.id}`}
                onClick={() => fnOrderStatsHandler(selectedOrder)}
              >
                <EqualizerOutlinedIcon fontSize='small' />
                &nbsp;&nbsp;Order Stats
              </MenuItem>
              <Divider />
            </Menu>
          </>
        );
      },
    },
  ];

  const columns = [...(transportationMode === 'OTR' ? OTR_COLUMNS : INTERMODAL_COLUMNS), ...ACTIONS];
  return (
    <Box
      m='20px 0 0 0'
      height='75vh'
    >
      <StyledDataGrid
        rows={orders}
        columns={columns}
        components={{ Toolbar: GridToolbar }}
      />{' '}
      {/* Data Grid */}
    </Box>
  );
};

export default DispatchTableView;